import { Grid, Popover } from '@material-ui/core';
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Icons,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { Discipline, InspectionTaskListType, ITaskWiseComments } from '../../models/models';
import useDeficiencyOnInspectionLevel from '../../rxdb/deficiencyList/useDeficiencyOnInspectionLevel';
import { usePreviousTaskComments } from '../../rxdb/previousTaskComments/usePreviousTaskComments';
import { TaskList } from '../../rxdb/taskList/queryBuilder';
import { ViewInspectorDocument } from '../../rxdb/viewInspector/queryBuilder';
import { InspectionStatus, TaskStatus } from '../../utilities/enums';
import {
  checkInspectionStatusForUpdate,
  checkTaskStatusForReAssign,
  createObjectForBulkReAssignAndSign,
  getStatus,
  getStatusForInspection,
  setValueinLocalStorage,
} from '../../utilities/utilityFunctions';
import AdhocInspectionAssignment from '../AssignInspector/AdhocInspectionAssignment';
import AssignInspector from '../AssignInspector/AssignInspector';
import ReAssignInspector from '../AssignInspector/ReAssignInspector';
import CommentToolTip from '../CommentToolTip';
import DeleteTask from '../DeleteTask';
import SignAndStamp from '../InspectionListSignAndStamp';
import StatusLabel from '../StatusLabel/StatusLabel';
import useStyles from './Approval.styles';

interface Props {
  inspectionData: InspectionTaskListType;
  inspectorData: ViewInspectorDocument[];
  renderAccordian: (
    id: string,
    statusId: number,
    label: React.ReactElement,
    content: (false | JSX.Element)[] | undefined,
  ) => JSX.Element;
  isAssignToLead: boolean;
}

const Approval: React.FC<Props> = ({
  inspectionData,
  inspectorData,
  renderAccordian,
  isAssignToLead,
}) => {
  const portrait = window.matchMedia('(orientation: portrait)').matches;
  const classes = useStyles({ portrait });
  const { HandleGesture, CurrentUserSelected, WorkPackageTabs, SiteDiscipline } = useMst();
  const history = useHistory();
  const [signAndStampFlag, setSignAndStampFlag] = useState<boolean>(false);
  const [openReAssignInspector, setOpenReAssignInspector] = useState<boolean>(false);
  const [taskId, setTaskId] = useState(0);
  const [deleteTaskData, setDeleteTaskData] = useState<TaskList>({} as TaskList);
  const [taskDeletionConfirmationOpen, setTaskDeletionConfirmationOpen] = useState<boolean>(false);
  const [isTaskDeletion, setIsTaskDeletion] = useState(false);
  const [openUpdateInspector, setOpenUpdateInspector] = useState(false);
  const [inspectionID, setInspectionID] = useState<number>(0);
  const [inspectionLevelComment, setInspectionLevelComment] = useState<string>('');
  const [actionAnchorEl, setActionAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [taskWiseCommentData, setTaskWiseCommentData] = useState<ITaskWiseComments>({});
  const [timeOutId, setTimeoutId] = useState<NodeJS.Timeout | null | number>(-1);
  const [midId, setMidId] = useState<number>(0);
  const [onHold, setOnHold] = useState<boolean>(false);
  const [isOpenAdhocReAssignInspector, setIsOpenAdhocReAssignInspector] = useState(false);
  const [isActionPopoverOpen, setIsActionPopoverOpen] = useState(false);
  const { assignBackComment, previousTaskCommentsCollection, previousTaskCommentsInitialized } =
    usePreviousTaskComments();
  const currentInspectorId = CurrentUserSelected?.currentUser?.inspectorId;
  const {
    assetName,
    assetId,
    areaId,
    inspectionId,
    inspectorId,
    areaList,
    inspectionAssignmentComment,
    assetInspectionType,
    assetInspectionStatusId,
    isAssignedBackTask,
    taskList,
    workPackageId,
    isAdhoc,
    inspectorNameList,
  } = inspectionData;
  const { deficiencies } = useDeficiencyOnInspectionLevel(Number(inspectionId));
  const isAssignedByDEC = !!(inspectorNameList && inspectorNameList[0]?.clerkId);
  const getTaskCommentsFromRxdb = async () => {
    for (const task in taskList) {
      if (
        taskList[Number(task)]?.fkInspectionTaskStatusCategory < TaskStatus.FI_SIGN_PENDING &&
        taskList[Number(task)]?.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED
      ) {
        const taskCommentsDocument = await assignBackComment(
          taskList[Number(task)]?.pkAssetInspectionTask,
        );
        if (taskCommentsDocument?.length) {
          const obj2: ITaskWiseComments = {};
          obj2[taskCommentsDocument[0]?.fkAssetInspectionTask] = {
            comment: taskCommentsDocument[0]?.comment,
            attentionFlag: taskCommentsDocument[0]?.attentionFlag,
          };
          setTaskWiseCommentData((curr: ITaskWiseComments) => {
            return { ...curr, ...obj2 };
          });
        }
      }
    }
  };

  const handleTaskClick = (task: TaskList) => {
    const {
      pkAssetInspectionTask,
      fkInspectorField,
      fkInspectorLead,
      fkInspectionTaskStatusCategory,
    } = task;
    setValueinLocalStorage(
      'assetId',
      SiteDiscipline.disciplineId === Discipline.Shaft ? areaId : assetId,
    );
    setValueinLocalStorage('inspectionID', inspectionId);
    setValueinLocalStorage('inspectorId', inspectorId);
    setValueinLocalStorage('taskId', String(pkAssetInspectionTask));
    CurrentUserSelected.setInspectionId(Number(inspectionId));
    CurrentUserSelected.setTaskId(pkAssetInspectionTask);
    CurrentUserSelected.setIsAdhoc(isAdhoc || false);
    CurrentUserSelected.setAssetId(
      SiteDiscipline.disciplineId === Discipline.Shaft ? areaId : assetId,
    );
    setValueinLocalStorage('taskFieldInspectorId', String(fkInspectorField));
    setValueinLocalStorage('taskLeadInspectorId', String(fkInspectorLead));
    setValueinLocalStorage('taskStatus', String(fkInspectionTaskStatusCategory));
    setValueinLocalStorage('inspectionStatus', assetInspectionStatusId);
    setValueinLocalStorage('isAdhoc', isAdhoc);
    if (isAdhoc) {
      setValueinLocalStorage('workPackageId', String(workPackageId));
      CurrentUserSelected.setWorkPackageId(workPackageId);
    }
    history.push('/inspection-details');
  };

  const handleReAssign = (e: { stopPropagation: () => void }, tId: number) => {
    setTaskId(tId);
    setActionAnchorEl(null);
    if (WorkPackageTabs.tab === 1) setIsOpenAdhocReAssignInspector(true);
    else setOpenReAssignInspector(true);
    e.stopPropagation();
  };

  const handleUpdateInspectors = (e: { stopPropagation: () => void }, tId: number) => {
    setTaskId(tId || 0);
    setInspectionID(Number(inspectionId));
    setInspectionLevelComment(inspectionAssignmentComment || '');
    setActionAnchorEl(null);
    setOpenUpdateInspector(true);
    e.stopPropagation();
  };

  const handleDeleteTask = (e: { stopPropagation: () => void }, task: TaskList, val: boolean) => {
    setDeleteTaskData(task);
    setTaskDeletionConfirmationOpen(true);
    setIsTaskDeletion(val);
    e.stopPropagation();
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
  ) => {
    setActionAnchorEl(event.currentTarget);
    setIsActionPopoverOpen(true);
    event.stopPropagation();
  };

  const handleActionClose = (e: { stopPropagation: () => void }) => {
    setActionAnchorEl(null);
    setIsActionPopoverOpen(false);
    e.stopPropagation();
  };

  const checkSignAndStamp = (inspectionStatusId: number) => {
    return (
      inspectionStatusId === InspectionStatus.LI_SIGN_PENDING &&
      currentInspectorId === (taskList as TaskList[])[0]?.fkInspectorLead
    );
  };

  const handleInspectionClick = () => {
    setValueinLocalStorage(
      'assetId',
      SiteDiscipline.disciplineId === Discipline.Shaft ? areaId : assetId,
    );
    setValueinLocalStorage('inspectionID', inspectionId);
    CurrentUserSelected.setInspectionId(Number(inspectionId));
    CurrentUserSelected.setAssetId(
      SiteDiscipline.disciplineId === Discipline.Shaft ? areaId : assetId,
    );
    setValueinLocalStorage('inspectorId', inspectorId);
  };

  const handleLISignAndStamp = (e: { stopPropagation: () => void }) => {
    setActionAnchorEl(null);
    setSignAndStampFlag(true);
    handleInspectionClick();
    e.stopPropagation();
  };

  const checkFISignAndStamp = (taskStatusId: number, fieldInspectorId: number) => {
    return (
      assetInspectionStatusId === InspectionStatus.FI_SIGN_PENDING &&
      taskStatusId === TaskStatus.FI_SIGN_PENDING &&
      currentInspectorId === fieldInspectorId
    );
  };

  const checkLISignAndStamp = (leadInspectorId: number) => {
    return (
      assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING &&
      currentInspectorId === leadInspectorId
    );
  };

  const isTaskEnableForReAssign = (taskStatusId: number) => taskStatusId !== TaskStatus.COMPLETED;

  const checkforInspection = () => {
    if (HandleGesture.gestureForSignAndStamp) {
      if (assetInspectionStatusId === InspectionStatus.FI_SIGN_PENDING) {
        if (
          taskList
            ?.filter(
              task =>
                task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING &&
                currentInspectorId === task.fkInspectorField,
            )
            .every(task => HandleGesture.taskIds.includes(task.pkAssetInspectionTask))
        ) {
          HandleGesture.insertIntoInspectionId(Number(inspectionId));
        } else {
          HandleGesture.removeFromInspectionId(Number(inspectionId));
        }
      } else if (
        HandleGesture.inspectionIds.find(element => element === Number(inspectionId)) !== undefined
      ) {
        HandleGesture.removeFromInspectionId(Number(inspectionId));
      } else {
        HandleGesture.insertIntoInspectionId(Number(inspectionId));
      }
    }
    if (HandleGesture.gestureForReAssign) {
      if (
        taskList
          ?.filter(task => task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED)
          .every(task => HandleGesture.taskIds.includes(task.pkAssetInspectionTask))
      ) {
        HandleGesture.insertIntoInspectionId(Number(inspectionId));
      } else {
        HandleGesture.removeFromInspectionId(Number(inspectionId));
      }
    }
  };

  const checkforTask = (insert: boolean) => {
    let isEntryDone = false;
    if (HandleGesture.gestureForSignAndStamp) {
      taskList?.forEach((task: TaskList) => {
        // Bulk FI sign and stamp
        if (
          task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING &&
          currentInspectorId === task.fkInspectorField
        ) {
          if (insert) {
            HandleGesture.insertIntoAccordion(String(task.fkAssetInspection));
            HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
              true,
            );
          } else {
            HandleGesture.removeFromTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
              false,
            );
          }
        }
        // Bulk LI sign and stamp
        else if (
          task.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING &&
          assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING &&
          currentInspectorId === task.fkInspectorLead &&
          !isEntryDone
        ) {
          isEntryDone = true;
          if (insert) {
            HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
              true,
            );
          } else {
            HandleGesture.removeFromTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
              false,
            );
          }
        }
      });
    }
    if (HandleGesture.gestureForReAssign) {
      taskList?.forEach((task: TaskList) => {
        if (task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED) {
          if (insert) {
            HandleGesture.insertIntoAccordion(String(task.fkAssetInspection));
            HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
              true,
            );
          } else {
            HandleGesture.removeFromTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
              false,
            );
          }
        }
      });
    }
  };

  const setValuesInMobX = (task: TaskList) => {
    setOnHold(true);
    HandleGesture.insertIntoAccordion(String(task.fkAssetInspection));
    HandleGesture.insertIntoTaskId(task.pkAssetInspectionTask);
    HandleGesture.setTasksForReAssignAndSign(
      createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
      true,
    );

    HandleGesture.setInspectionStatusId(assetInspectionStatusId);
    checkforInspection();
  };

  const handleInspectionCheckBoxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    if (checked) {
      HandleGesture.insertIntoInspectionId(Number(inspectionId));
      checkforTask(checked);
    } else {
      HandleGesture.removeFromInspectionId(Number(inspectionId));
      checkforTask(checked);
    }
    event.stopPropagation();
  };

  const onPress = (task: TaskList) => {
    setMidId(task.pkAssetInspectionTask);
    if (!HandleGesture.bulkUpload) {
      const tid: NodeJS.Timeout | null = setTimeout(() => {
        let tempTask = null;
        if (
          checkFISignAndStamp(task.fkInspectionTaskStatusCategory, task.fkInspectorField) ||
          checkLISignAndStamp(task.fkInspectorLead)
        ) {
          HandleGesture.setGestureForSignAndStamp(true);
          tempTask = task;
        }
        if (isTaskEnableForReAssign(task.fkInspectionTaskStatusCategory)) {
          HandleGesture.setGestureForReAssign(true);
          tempTask = task;
        }
        if (tempTask) setValuesInMobX(tempTask);
      }, 1000);
      setTimeoutId(tid);
    }
  };

  const onClickUp = (task: TaskList) => {
    if (
      (HandleGesture.bulkUpload &&
        midId === Number(task.pkAssetInspectionTask) &&
        (checkFISignAndStamp(task.fkInspectionTaskStatusCategory, task.fkInspectorField) ||
          isTaskEnableForReAssign(task.fkInspectionTaskStatusCategory))) ||
      checkLISignAndStamp(task.fkInspectorLead)
    ) {
      if (
        HandleGesture.taskIds.find(element => element === Number(task.pkAssetInspectionTask)) !==
        undefined
      ) {
        HandleGesture.removeFromTaskId(Number(task.pkAssetInspectionTask));
        HandleGesture.setTasksForReAssignAndSign(
          createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
          false,
        );
      } else {
        HandleGesture.insertIntoAccordion(String(task.fkAssetInspection));
        HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
        HandleGesture.setTasksForReAssignAndSign(
          createObjectForBulkReAssignAndSign(task, assetInspectionStatusId),
          true,
        );
      }
      checkforInspection();
    }
    if (!HandleGesture.bulkUpload) {
      clearTimeout(timeOutId as NodeJS.Timeout);
      if (midId === Number(task.pkAssetInspectionTask)) {
        handleTaskClick(task);
      }
    }
  };

  const onRelease = (e: React.TouchEvent<HTMLDivElement>, task: TaskList) => {
    onClickUp(task);
    e.preventDefault();
  };

  const showTask = (task: TaskList) =>
    !HandleGesture.bulkUpload || task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED;

  const onMove = () => {
    if (onHold) setOnHold(false);
    setMidId(-1);
  };

  const checkSignAndStampButton = useMemo(() => {
    return (
      (taskList as TaskList[])?.filter(
        (task: TaskList) =>
          task.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING && task.attentionFlag,
      ).length > 0 || deficiencies.filter(deficiency => deficiency?.attentionFlag).length > 0
    );
  }, [taskList, deficiencies]);

  const MainInspectionGrid = (dropDown: boolean) => {
    return (
      <Grid item container xs={12} alignItems="center">
        <Grid item container xs={6} className={classes.checkbox}>
          {HandleGesture.bulkUpload && (
            <Grid
              item
              onMouseUp={e => {
                e.stopPropagation();
              }}
              className={classes.checkbox1}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="multiselect-checkbox-approval"
                    checked={
                      HandleGesture.inspectionIds.find(
                        element => element === Number(inspectionId),
                      ) !== undefined
                    }
                    onChange={handleInspectionCheckBoxClick}
                  />
                }
                label=""
              />
            </Grid>
          )}
          <Grid item className={classes.inspectionDetails}>
            <Typography variant="h5" display="inline">
              {`Id: ${inspectionId} - ${assetName}`}
            </Typography>
            <Typography className={classes.greenDot} />
            <Typography
              display="inline"
              variant="h5"
              fontWeight="normal"
              className={classes.locationStyle}
            >
              {areaList?.length > 0 ? areaList[0]?.name : ''}
            </Typography>
            {!dropDown &&
              taskList &&
              taskList[0]?.fkInspectionTaskStatusCategory < TaskStatus.FI_SIGN_PENDING &&
              taskList[0]?.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED && (
                <CommentToolTip
                  comment={taskWiseCommentData[taskList[0]?.pkAssetInspectionTask]?.comment}
                  attentionFlag={
                    taskWiseCommentData[taskList[0]?.pkAssetInspectionTask]?.attentionFlag
                  }
                />
              )}
            {inspectionAssignmentComment && (
              <Typography variant="subtitle2" fontWeight="normal">
                {inspectionAssignmentComment}
              </Typography>
            )}
            <Typography variant="subtitle2" fontWeight="normal" className={classes.locationStyle}>
              {assetInspectionType}
              <Typography className={`${classes.greenDot}`} />
              {taskList &&
                (!dropDown
                  ? `LI: ${taskList[0]?.leadInspectorName || ''}, ${
                      taskList[0]?.name ? 'FI: ' : 'DEC: '
                    }
                    ${taskList[0]?.name || taskList[0]?.clerkName || ''}`
                  : `LI: ${taskList[0]?.leadInspectorName || ''}`)}
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs={2} className={classes.statusAndButtons}>
          {checkInspectionStatusForUpdate(assetInspectionStatusId) &&
            isAssignToLead &&
            !HandleGesture.gestureForReAssign &&
            dropDown && (
              <Grid container className={classes.updateBtnGrid}>
                <Button
                  color="primary"
                  className="updateBtn"
                  noMinHeight
                  onClick={handleUpdateInspectors}
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                >
                  <Typography variant="subtitle2">Update Inspectors</Typography>
                </Button>
              </Grid>
            )}
          {checkInspectionStatusForUpdate(assetInspectionStatusId) &&
            isAssignToLead &&
            !HandleGesture.gestureForReAssign &&
            !dropDown && (
              <>
                <Grid
                  container
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  className={classes.actionButtonGrid}
                >
                  <Button
                    data-testid="approval-action-button"
                    color="primary"
                    className="actionButton"
                    noMinHeight
                    onMouseUp={e => openActionMenu(e)}
                    onMouseDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    onTouchEnd={e => openActionMenu(e)}
                    endAdornment={<Icons.ChevronDownFeather color="primary" size="small" />}
                  >
                    <Typography variant="subtitle2">Action</Typography>
                  </Button>
                </Grid>
                <Popover
                  id="action"
                  open={Boolean(actionAnchorEl)}
                  anchorEl={actionAnchorEl}
                  onClose={handleActionClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  classes={{ paper: classes.paper }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Grid
                        container
                        className={classes.button}
                        onMouseUp={e => {
                          if (!isAssignedByDEC) {
                            handleReAssign(e, (taskList as TaskList[])[0]?.pkAssetInspectionTask);
                          } else {
                            handleUpdateInspectors(
                              e,
                              (taskList as TaskList[])[0]?.pkAssetInspectionTask,
                            );
                          }
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onTouchEnd={e => {
                          if (!isAssignedByDEC) {
                            handleReAssign(e, (taskList as TaskList[])[0]?.pkAssetInspectionTask);
                          } else {
                            handleUpdateInspectors(
                              e,
                              (taskList as TaskList[])[0]?.pkAssetInspectionTask,
                            );
                          }
                        }}
                      >
                        <Typography data-testid="re-assign-approval" variant="subtitle2">
                          Re-Assign
                        </Typography>
                      </Grid>
                    </Grid>
                    {!isAssignedByDEC && (
                      <Grid item xs={12}>
                        <Grid
                          container
                          className={classes.button}
                          onMouseUp={handleUpdateInspectors}
                          onMouseDown={e => e.stopPropagation()}
                          onTouchStart={e => e.stopPropagation()}
                          onTouchEnd={handleUpdateInspectors}
                        >
                          <Typography data-testid="update-inspector-approval" variant="subtitle2">
                            Update Inspectors
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Grid
                        container
                        onMouseUp={e => handleDeleteTask(e, (taskList as TaskList[])[0], false)}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onTouchEnd={e => handleDeleteTask(e, (taskList as TaskList[])[0], false)}
                        className={classes.button}
                      >
                        <Typography
                          data-testid="delete-task-approval"
                          variant="subtitle2"
                          color="error"
                        >
                          Delete Inspection
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Popover>
              </>
            )}
          {!HandleGesture.bulkUpload && checkSignAndStamp(assetInspectionStatusId) && (
            <>
              <Grid
                container
                justifyContent="flex-end"
                alignItems="flex-end"
                className={classes.actionButtonGrid}
              >
                <Button
                  color="primary"
                  className="actionButton"
                  noMinHeight
                  onMouseUp={openActionMenu}
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                  onTouchEnd={openActionMenu}
                  endAdornment={<Icons.ChevronDownFeather color="primary" size="small" />}
                >
                  <Typography variant="subtitle2">Action</Typography>
                </Button>
              </Grid>
              <Popover
                id="action"
                open={Boolean(actionAnchorEl)}
                anchorEl={actionAnchorEl}
                onClose={handleActionClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                classes={{ paper: classes.paper }}
              >
                <Grid container spacing={1}>
                  {!dropDown && (
                    <Grid item xs={12}>
                      <Grid
                        container
                        className={classes.button}
                        onMouseUp={e =>
                          handleReAssign(e, (taskList as TaskList[])[0]?.pkAssetInspectionTask)
                        }
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onTouchEnd={e =>
                          handleReAssign(e, (taskList as TaskList[])[0]?.pkAssetInspectionTask)
                        }
                      >
                        <Typography variant="subtitle2">Re-Assign</Typography>
                      </Grid>
                    </Grid>
                  )}
                  {!isAdhoc && (
                    <Grid item xs={12}>
                      <Grid
                        container
                        className={classes.button}
                        onMouseUp={handleUpdateInspectors}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onTouchEnd={handleUpdateInspectors}
                      >
                        <Typography data-testid="update-inspector-approval" variant="subtitle2">
                          Update Inspectors
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid
                      container
                      className={!checkSignAndStampButton ? classes.button : classes.buttonDisabled}
                      onMouseUp={e => !checkSignAndStampButton && handleLISignAndStamp(e)}
                      onMouseDown={e => e.stopPropagation()}
                      onTouchStart={e => e.stopPropagation()}
                      onTouchEnd={e => !checkSignAndStampButton && handleLISignAndStamp(e)}
                    >
                      <Typography variant="subtitle2">Sign & Stamp</Typography>
                    </Grid>
                  </Grid>
                  {!dropDown && (
                    <Grid item xs={12}>
                      <Grid
                        container
                        onMouseUp={e => handleDeleteTask(e, (taskList as TaskList[])[0], false)}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onTouchEnd={e => handleDeleteTask(e, (taskList as TaskList[])[0], false)}
                        className={classes.button}
                      >
                        <Typography
                          data-testid="delete-task-approval"
                          variant="subtitle2"
                          color="error"
                        >
                          Delete Inspection
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Popover>
            </>
          )}
          {!HandleGesture.bulkUpload &&
            ((isAdhoc &&
              (assetInspectionStatusId === InspectionStatus.FI_SIGN_PENDING ||
                (assetInspectionStatusId === InspectionStatus.LI_SIGN_PENDING &&
                  taskList?.length &&
                  taskList[0].fkInspectorLead !== currentInspectorId))) ||
              (!isAssignToLead &&
                (assetInspectionStatusId === InspectionStatus.LI_PENDING_APPROVAL ||
                  assetInspectionStatusId === InspectionStatus.SC_PENDING_APPROVAL ||
                  assetInspectionStatusId === InspectionStatus.FI_SIGN_PENDING))) &&
            !dropDown && (
              <Grid container alignItems="flex-end" justifyContent="flex-end">
                <div className={classes.reAssignButtonForFI}>
                  <Button
                    color="primary"
                    className="reAssignButton"
                    noMinHeight
                    onMouseUp={e => {
                      handleReAssign(e, (taskList as TaskList[])[0]?.pkAssetInspectionTask);
                    }}
                    onMouseDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    onTouchEnd={e => {
                      handleReAssign(e, (taskList as TaskList[])[0]?.pkAssetInspectionTask);
                      e.preventDefault();
                    }}
                  >
                    <Typography variant="subtitle2">Re-Assign</Typography>
                  </Button>
                </div>
              </Grid>
            )}
        </Grid>
        <Grid item xs={4} className={classes.statusAndButtons}>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <StatusLabel
                inspectionStatusId={assetInspectionStatusId}
                customClass="singleStatusStyleApproval"
                label={getStatusForInspection(assetInspectionStatusId)}
              />
              {isAssignedBackTask ? (
                <Grid className={classes.assignedBackGrid}>
                  <Typography variant="subtitle1" color="error">
                    Assigned back with Review findings
                  </Typography>
                </Grid>
              ) : (
                ''
              )}
            </Grid>
            {!dropDown && <Icons.ChevronRightFeather color="primary" />}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    let pcSub: Subscription;
    if (taskList?.length && previousTaskCommentsInitialized) {
      getTaskCommentsFromRxdb();
      if (previousTaskCommentsCollection) {
        pcSub = previousTaskCommentsCollection.$.subscribe(() => {
          getTaskCommentsFromRxdb();
        });
      }
    }
    return () => {
      if (pcSub && pcSub.unsubscribe) pcSub.unsubscribe();
    };
  }, [previousTaskCommentsInitialized, taskList]);

  return (
    <>
      <Grid className={classes.approvalContainer}>
        {(taskList as TaskList[])?.length < 2 ? (
          <Card
            data-testid={`${'inspection-div-approval'}-${inspectionId}`}
            elevation={1}
            className={`${classes.outerCard} ${
              HandleGesture.inspectionIds.find(id => id === Number(inspectionId)) !== undefined
                ? 'cardWithColor'
                : ''
            }`}
            key={inspectionId}
            onMouseDown={() => {
              if (!isActionPopoverOpen) onPress((taskList as TaskList[])[0]);
            }}
            onMouseMove={onMove}
            onMouseUp={e => {
              const task = (taskList as TaskList[])[0];
              if (task?.pkAssetInspectionTask) setMidId(task.pkAssetInspectionTask);
              if (onHold) setOnHold(false);
              else if (isActionPopoverOpen) handleActionClose(e);
              else onClickUp(task);
            }}
            onTouchStart={() => onPress((taskList as TaskList[])[0])}
            onTouchMove={onMove}
            onTouchEnd={e => {
              if (onHold) setOnHold(false);
              else onRelease(e, (taskList as TaskList[])[0]);
            }}
          >
            <Grid className={classes.pointer1}>{MainInspectionGrid(false)}</Grid>
          </Card>
        ) : (
          <Card
            data-testid={`${'inspection-div-approval'}-${inspectionId}`}
            elevation={1}
            key={inspectionId}
            className={
              HandleGesture.handleAccordion.find(element => element === inspectionId) ===
                undefined &&
              HandleGesture.inspectionIds.find(id => id === Number(inspectionId)) !== undefined
                ? 'cardWithColor'
                : ''
            }
          >
            <Grid className={classes.accordionOuterGrid}>
              {renderAccordian(
                inspectionId,
                assetInspectionStatusId,
                MainInspectionGrid(true),
                taskList
                  ?.filter(task => showTask(task))
                  ?.map((task: TaskList) => {
                    return (
                      <Card
                        key={String(task.pkAssetInspectionTask)}
                        id={String(task.pkAssetInspectionTask)}
                        elevation={1}
                        className={`taskOuterGrid ${
                          HandleGesture.taskIds.find(
                            element => element === Number(task.pkAssetInspectionTask),
                          ) !== undefined
                            ? 'cardWithColor'
                            : ''
                        }`}
                        onMouseDown={() => onPress(task)}
                        onMouseMove={onMove}
                        onMouseUp={() => {
                          if (task?.pkAssetInspectionTask) setMidId(task.pkAssetInspectionTask);
                          if (onHold) setOnHold(false);
                          else onClickUp(task);
                        }}
                        onTouchStart={() => onPress(task)}
                        onTouchMove={onMove}
                        onTouchEnd={e => {
                          if (onHold) setOnHold(false);
                          else onRelease(e, task);
                        }}
                      >
                        <Grid className={classes.pointer}>
                          <Grid item container xs={12} alignItems="center">
                            <Grid item container xs={6} className={classes.taskCheckbox1}>
                              {HandleGesture.bulkUpload && (
                                <Grid item>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          HandleGesture.taskIds.find(
                                            element =>
                                              element === Number(task.pkAssetInspectionTask),
                                          ) !== undefined
                                        }
                                      />
                                    }
                                    label=""
                                  />
                                </Grid>
                              )}
                              <Grid item className={classes.taskDetails}>
                                <Typography variant="h6" display="inline">
                                  {`Task Id: ${task.pkAssetInspectionTask}`}
                                </Typography>
                                {task?.fkInspectionTaskStatusCategory <
                                  TaskStatus.FI_SIGN_PENDING &&
                                  task?.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED && (
                                    <CommentToolTip
                                      comment={
                                        taskWiseCommentData[task?.pkAssetInspectionTask]?.comment
                                      }
                                      attentionFlag={
                                        taskWiseCommentData[task?.pkAssetInspectionTask]
                                          ?.attentionFlag
                                      }
                                    />
                                  )}
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="normal"
                                  className={classes.locationStyle}
                                >
                                  {task.taskDescription}
                                </Typography>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="normal"
                                  className={classes.locationStyle}
                                >
                                  {`FI: ${task.name}`}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid item xs={2} className={classes.statusAndButtons}>
                              {!HandleGesture.gestureForReAssign &&
                                checkTaskStatusForReAssign(
                                  task?.fkInspectionTaskStatusCategory,
                                ) && (
                                  <Grid
                                    container
                                    justifyContent="flex-end"
                                    alignItems="flex-end"
                                    className={classes.reAssignButtonGrid}
                                  >
                                    <Button
                                      color="primary"
                                      className="reAssignButton"
                                      noMinHeight
                                      onMouseUp={e => handleReAssign(e, task.pkAssetInspectionTask)}
                                      onMouseDown={e => e.stopPropagation()}
                                      onTouchStart={e => e.stopPropagation()}
                                      onTouchEnd={e =>
                                        handleReAssign(e, task.pkAssetInspectionTask)
                                      }
                                    >
                                      <Typography variant="subtitle2">Re-Assign</Typography>
                                    </Button>
                                  </Grid>
                                )}
                            </Grid>
                            <Grid item xs={4} className={classes.statusAndButtons}>
                              <Grid container justifyContent="flex-end" alignItems="center">
                                <Grid item>
                                  <StatusLabel
                                    taskStatusId={task.fkInspectionTaskStatusCategory}
                                    customClass={
                                      checkTaskStatusForReAssign(
                                        task.fkInspectionTaskStatusCategory,
                                      ) &&
                                      isAssignToLead &&
                                      !HandleGesture.gestureForReAssign
                                        ? 'statusStyleApproval'
                                        : 'reAssignButtonRemovalApproval'
                                    }
                                    label={getStatus(task.fkInspectionTaskStatusCategory)}
                                  />
                                </Grid>
                                {checkTaskStatusForReAssign(task.fkInspectionTaskStatusCategory) &&
                                  isAssignToLead && (
                                    <Grid
                                      item
                                      onMouseUp={e => handleDeleteTask(e, task, true)}
                                      onMouseDown={e => e.stopPropagation()}
                                      onTouchStart={e => e.stopPropagation()}
                                      onTouchEnd={e => handleDeleteTask(e, task, true)}
                                      className={
                                        !HandleGesture.gestureForReAssign
                                          ? classes.trashIcon
                                          : classes.trashIconRemoval
                                      }
                                    >
                                      <Icons.Trash2Feather color="error" />
                                    </Grid>
                                  )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    );
                  }),
              )}
            </Grid>
          </Card>
        )}
      </Grid>
      {signAndStampFlag && (
        <Grid>
          <SignAndStamp
            openSignAndStamp={signAndStampFlag}
            setopenSignAndStamp={setSignAndStampFlag}
            inspectionData={inspectionData}
            isFISignAndStamp={false}
            anchorEl={null}
            isSignAndStampFromInspectionList
          />
        </Grid>
      )}
      {openReAssignInspector && (
        <ReAssignInspector
          openAssignInspector={openReAssignInspector}
          setOpenAssignInspector={setOpenReAssignInspector}
          taskId={taskId}
          workPackageId={workPackageId}
        />
      )}
      {isOpenAdhocReAssignInspector && (
        <AdhocInspectionAssignment
          open={isOpenAdhocReAssignInspector}
          onClose={() => setIsOpenAdhocReAssignInspector(false)}
          taskId={taskId}
        />
      )}
      {taskDeletionConfirmationOpen && (
        <DeleteTask
          open={taskDeletionConfirmationOpen}
          onClose={() => setTaskDeletionConfirmationOpen(false)}
          taskData={deleteTaskData}
          isInspectorDeletion={false}
          isTaskDeletion={isTaskDeletion}
          onDeleteInspector={() => {}}
        />
      )}
      {openUpdateInspector && (
        <AssignInspector
          title="Update Inspector"
          isUpdateInspector={false}
          openAssignInspector={openUpdateInspector}
          setOpenAssignInspector={setOpenUpdateInspector}
          fieldInspectorList={[
            {
              fieldInspectorName: 'Assigned To Me',
              fieldInspectorId: currentInspectorId,
            },
            ...JSON.parse(JSON.stringify(inspectorData)),
          ]}
          leadInspectorId={taskList[0].fkInspectorLead}
          inspectionId={inspectionID}
          taskList={taskList}
          inspectionLevelComment={inspectionLevelComment}
          isAssignToLead={isAssignToLead}
          taskId={taskId}
        />
      )}
    </>
  );
};
export default observer(Approval);
